import type { AreaType } from '../context/AreaContext';

/** Rotas que pertencem exclusivamente à área de Compras. */
const PURCHASES_PATH_PREFIXES = [
  '/purchases',
  '/suppliers',
  '/documents',
  '/approvals',
  '/purchase-export',
  '/document-types',
] as const;

const CHANGES_PATH_PREFIXES = ['/changes'] as const;

/** Rotas que pertencem exclusivamente à área de Incidentes (tickets). */
const TICKETS_PATH_PREFIXES = ['/tickets', '/ticket-export', '/admin/tickets-attachments-without-s3'] as const;

/** Rotas exclusivas da Área de Projetos (PMO / Jira). */
const PROJECTS_PATH_PREFIXES = ['/projects'] as const;

export function getAreaFromPath(pathname: string): AreaType | null {
  if (PURCHASES_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
    return 'purchases';
  }
  if (CHANGES_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
    return 'changes';
  }
  if (TICKETS_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
    return 'tickets';
  }
  if (PROJECTS_PATH_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))) {
    return 'projects';
  }
  return null;
}

/** Home da área ativa no menu de módulos. */
export function getAreaHomePath(area: AreaType): string {
  switch (area) {
    case 'projects':
      return '/projects';
    case 'changes':
      return '/changes';
    case 'purchases':
      return '/purchases';
    case 'tickets':
    default:
      return '/dashboard';
  }
}

/** Dashboard operacional (incidentes e compras) — não pertence a mudanças/projetos. */
export function isOperationalDashboardPath(pathname: string): boolean {
  return pathname === '/dashboard';
}

/** Home de incidentes na raiz — redireciona outros módulos para a home deles. */
export function isTicketsModuleHomePath(pathname: string): boolean {
  return pathname === '/dashboard' || pathname === '/';
}

export function isEntityDetailPath(pathname: string): boolean {
  return (
    /^\/tickets\/\d+/.test(pathname) ||
    /^\/purchases\/\d+/.test(pathname) ||
    /^\/changes\/\d+/.test(pathname) ||
    /^\/purchases\/opportunities\/\d+/.test(pathname)
  );
}

/**
 * Se a URL atual não combina com a área escolhida, retorna a home da área (para redirect).
 */
export function getRedirectIfAreaPathMismatch(pathname: string, area: AreaType): string | null {
  const routeArea = getAreaFromPath(pathname);
  if (routeArea !== null && routeArea !== area) {
    return getAreaHomePath(area);
  }
  if (isOperationalDashboardPath(pathname) && (area === 'tickets' || area === 'purchases')) {
    return null;
  }
  if (isTicketsModuleHomePath(pathname) && area !== 'tickets') {
    return getAreaHomePath(area);
  }
  return null;
}

export function shouldLeaveRouteOnAreaChange(pathname: string, newArea: AreaType): boolean {
  const mismatch = getRedirectIfAreaPathMismatch(pathname, newArea);
  if (mismatch !== null && mismatch !== pathname) {
    return true;
  }
  if (isEntityDetailPath(pathname)) {
    const detailArea: AreaType | null = pathname.startsWith('/tickets')
      ? 'tickets'
      : pathname.startsWith('/purchases')
        ? 'purchases'
        : pathname.startsWith('/changes')
          ? 'changes'
          : null;
    return detailArea !== null && detailArea !== newArea;
  }
  return false;
}
